import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = [
  '#6CB4B0', '#7BBDB8', '#8EC8C4', '#9DD3CF', '#AEE0DC',
  '#B8A9D9', '#C5BAE2', '#D1CCEA', '#DDD6F3', '#E8E4F8',
];

const SECTION_COLORS = {
  top_courses:                 '#5BA3A0',
  top_sales_ready:             '#5BA3A0',
  top_tech_deal_ready:         '#5BA3A0',
  top_project_ready:           '#8B77C8',
  top_project_ready_with_labs: '#8B77C8',
  top_projects:                '#5BA3A0',
  top_featured_products:       '#C87598',
  top_regions:                 '#5BA3A0',
};

function truncate(str, n = 22) {
  if (!str) return '';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

function ChartCard({ title, data, colorKey }) {
  const color = SECTION_COLORS[colorKey] || '#5BA3A0';
  if (!data || data.length === 0) {
    return (
      <div className="chart-card">
        <div className="chart-title">{title}</div>
        <div style={{ padding: '20px 0', color: '#999', fontSize: 13, textAlign: 'center' }}>No data available</div>
      </div>
    );
  }
  const chartData = [...data].reverse().map(d => ({ ...d, shortName: truncate(d.name) }));
  return (
    <div className="chart-card" data-testid={`chart-${colorKey}`}>
      <div className="chart-title">{title}</div>
      <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 28)}>
        <BarChart layout="vertical" data={chartData}
          margin={{ top: 4, right: 50, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="shortName" width={130}
            tick={{ fontSize: 11 }} interval={0} />
          <Tooltip
            formatter={(v, n, p) => [v.toLocaleString(), 'Learners']}
            labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''}
          />
          <Bar dataKey="count" radius={[0, 3, 3, 0]} label={{ position: 'right', fontSize: 11 }}>
            {chartData.map((_, i) => (
              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function CourseEnrollmentsGraph() {
  const navigate = useNavigate();
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchGraph = async (fd, td) => {
    setLoading(true);
    try {
      const params = {};
      if (fd) params.from_date = fd;
      if (td) params.to_date = td;
      const r = await axios.get(`${API}/api/reports/course-activity/graph`,
        { params, withCredentials: true });
      setGraphData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchGraph('', ''); }, []);

  const handleApplyDate = () => fetchGraph(fromDate, toDate);
  const handleResetDate = () => { setFromDate(''); setToDate(''); fetchGraph('', ''); };

  const s = graphData?.stats || {};
  const c = graphData?.charts || {};

  const statCards = [
    { label: 'Total Enrollments',                value: s.total_enrollments },
    { label: 'Total Sales Ready Enrollments',    value: s.sales_ready },
    { label: 'Total Tech Deal Ready Enrollments', value: s.tech_deal_ready },
    { label: 'Total Project Ready Enrollments',  value: s.project_ready },
    { label: 'Total Project Ready with Labs Enrollments', value: s.project_ready_with_labs },
    { label: 'Total Credential Ready Enrollments', value: s.credential_ready },
  ];

  return (
    <div data-testid="course-enrollments-graph-page">
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        Dashboard &gt; Reports &gt; Course report &gt; Course Enrollments
      </div>

      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate('/reports/course-activity')}
          className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px' }}
          data-testid="back-btn">
          <ArrowLeft size={14} /> Back
        </button>
        <div>
          <h1 style={{ margin: 0 }}>Course Enrollments</h1>
          <p style={{ margin: 0, color: '#666', fontSize: 13 }}>A graph report of all enrollments</p>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 80, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : (
        <>
          {/* 6 Stat Cards */}
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 24 }}>
            {statCards.map((sc, i) => (
              <div key={i} className="stat-card" data-testid={`graph-stat-${i}`}>
                <div className="stat-value green">{(sc.value || 0).toLocaleString()}</div>
                <div className="stat-label">{sc.label}</div>
              </div>
            ))}
          </div>

          {/* Date Range Filter */}
          <div className="filter-bar" style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#555', alignSelf: 'flex-end', paddingBottom: 6 }}>
              Date Range
            </div>
            <div className="filter-group">
              <label className="filter-label">From</label>
              <input type="date" className="filter-input date" value={fromDate}
                onChange={e => setFromDate(e.target.value)} data-testid="graph-from-date" />
            </div>
            <div className="filter-group">
              <label className="filter-label">To</label>
              <input type="date" className="filter-input date" value={toDate}
                onChange={e => setToDate(e.target.value)} data-testid="graph-to-date" />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <button className="btn btn-primary" onClick={handleApplyDate} data-testid="graph-apply-btn">Apply</button>
              <button className="btn btn-secondary" onClick={handleResetDate} data-testid="graph-reset-btn">Reset</button>
            </div>
          </div>

          {/* Charts Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <ChartCard title="Top 10 Popular Courses" data={c.top_courses} colorKey="top_courses" />
            <ChartCard title="Top 10 Popular Sales Ready Courses" data={c.top_sales_ready} colorKey="top_sales_ready" />
            <ChartCard title="Top 10 Popular Tech Deal Ready Courses" data={c.top_tech_deal_ready} colorKey="top_tech_deal_ready" />
            <ChartCard title="Top 10 Popular Project Ready Courses" data={c.top_project_ready} colorKey="top_project_ready" />
            <ChartCard title="Top 10 Popular Project Ready with Lab Courses" data={c.top_project_ready_with_labs} colorKey="top_project_ready_with_labs" />
            <ChartCard title="Top 10 Popular Projects" data={c.top_projects} colorKey="top_projects" />
            <ChartCard title="Top 10 Popular Featured Products" data={c.top_featured_products} colorKey="top_featured_products" />
            <ChartCard title="Top A17 Regions" data={c.top_regions} colorKey="top_regions" />
          </div>
        </>
      )}
    </div>
  );
}
